import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import Card, { CardHeader, CardContent } from "@/components/shared/ui/Card";
import EmptyState from "@/components/shared/ui/EmptyState";
import {
  Globe,
  Linkedin,
  Twitter,
  Instagram,
  Facebook,
  Youtube,
  Music,
  PinIcon,
  BookOpen,
  ExternalLink,
} from "lucide-react";

interface DirectoryLink {
  label: string;
  url: string | null;
  icon: typeof Globe;
}

interface DirectoryWidgetProps {
  projectId: string;
}

export default function DirectoryWidget({ projectId }: DirectoryWidgetProps) {
  const [links, setLinks] = useState<DirectoryLink[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchDirectory() {
      try {
        const { data, error } = await supabase
          .from("projects")
          .select(
            "website_url_1, website_url_2, website_url_3, linkedin_url, twitter_url, instagram_url, facebook_url, youtube_url, tiktok_url, pinterest_url, substack_url"
          )
          .eq("id", projectId)
          .single();

        if (error) throw error;

        if (data) {
          const directoryLinks: DirectoryLink[] = [
            { label: "URL 1", url: data.website_url_1, icon: Globe },
            { label: "URL 2", url: data.website_url_2, icon: Globe },
            { label: "URL 3", url: data.website_url_3, icon: Globe },
            { label: "LinkedIn", url: data.linkedin_url, icon: Linkedin },
            { label: "Twitter", url: data.twitter_url, icon: Twitter },
            { label: "Instagram", url: data.instagram_url, icon: Instagram },
            { label: "Facebook", url: data.facebook_url, icon: Facebook },
            { label: "YouTube", url: data.youtube_url, icon: Youtube },
            { label: "TikTok", url: data.tiktok_url, icon: Music },
            { label: "Pinterest", url: data.pinterest_url, icon: PinIcon },
            { label: "Substack", url: data.substack_url, icon: BookOpen },
          ];

          // Filter out null/empty URLs
          setLinks(directoryLinks.filter((link) => link.url?.trim()));
        }
      } catch (error) {
        console.error("Error fetching directory links:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchDirectory();
  }, [projectId]);

  if (loading) {
    return (
      <Card variant="bordered">
        <CardHeader>
          <h3 className="text-base sm:text-lg font-bold text-black">
            Directory
          </h3>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (links.length === 0) {
    return (
      <Card variant="bordered">
        <CardHeader>
          <h3 className="text-base sm:text-lg font-bold text-black">
            Directory
          </h3>
        </CardHeader>
        <CardContent>
          <EmptyState
            title="No Links"
            description="No website or social links available for this project."
            icon={<Globe className="w-12 h-12" />}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="bordered">
      <CardHeader>
        <h3 className="text-base sm:text-lg font-bold text-black">Directory</h3>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {links.map((link, index) => {
            const Icon = link.icon;
            return (
              <a
                key={index}
                href={link.url!}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 rounded-lg border-2 border-black hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-5 h-5 text-gray-600" />
                  <span className="text-sm font-bold text-black">
                    {link.label}
                  </span>
                </div>
                <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-black transition-colors" />
              </a>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
